import { useState, useEffect } from 'react';
import { I18n } from '@aws-amplify/core';
import { useStepControl } from './useStepControl';
import { StepsResolution886 } from '../../../enums';
import { useGetActivities } from '@/hooks/useGetActivities';
import { useCreateActivity } from '@/hooks/useCreateActivity';
import { useResolution886Report } from '@/context/Resolution886Report';
import { useGetResolution886ReportReview, useUpdateResolution886Assessment } from '@/hooks/v2/resolution-886';
import type { ActivitiesValuesProps, AssessmentResponse } from '@/context/Resolution886Report/types';

interface EvaluationParams {
	goToSubStep: (step: number) => void;
}

interface Activity {
	id: string;
	name: string;
	status?: string;
}

interface UpdateResolution886AssessmentParams {
	organization_id: string;
	company_id: string;
	review_id: string;
	activity_id: string;
	file_id: string;
	assessment_id: string;
}

const BREAKPOINT_CONFIG = {
	XL_WIDTH: 1480,
	PAGINATION_LIMITS: { XL: 3, DEFAULT: 2 },
	TOAST_DURATION: 3000
} as const;

const DEFAULT_PAGE = 1;

export function useEvaluation({ goToSubStep }: EvaluationParams) {
	const { form, newReview, report, previousReview, setAssessment, handleSetCategoryResultId } =
		useResolution886Report();

	const { handleUpdateStep } = useStepControl();

	const { isXl } = useCustomBreakpoint();

	const [currentPage, setCurrentPage] = useState<number>(DEFAULT_PAGE);
	const [limit, setLimit] = useState<number>(
		isXl ? BREAKPOINT_CONFIG.PAGINATION_LIMITS.XL : BREAKPOINT_CONFIG.PAGINATION_LIMITS.DEFAULT
	);
	const [name, setName] = useState<string>('');
	const [selections, setSelections] = useState<(string | null)[]>([]);
	const [assessmentsNotAvaliated, setAssessmentsNotAvaliated] = useState<boolean>(false);
	const [previousAssessments, setPreviousAssessments] = useState<AssessmentResponse[]>([]);
	const [newActivities, setNewActivities] = useState<Activity[]>([]);

	const organization_id = form.getFieldValue(['general_information', 'organization_id']);
	const company_id = form.getFieldValue(['general_information', 'company_id']);
	const workstation_id = form.getFieldValue(['general_information', 'workstation_id']);
	const { activities: files_activities } = form.getFieldValue(['evaluation']);

	const { data: activities = [], isFetching: isFetchingActivities } = useGetActivities({
		organization_id,
		company_id,
		workstation_id
	});

	const { data: previousReviewData, isFetching: isFetchingPreviousReview } = useGetResolution886ReportReview({
		organization_id,
		company_id,
		report_id: report?.id,
		review_id: (previousReview && previousReview?.id) || ''
	});

	const { mutate: createActivity, isLoading: isLoadingCreateActivity } = useCreateActivity();
	const { mutate: updateAssessment, isLoading: isLoadingUpdateAssessment } = useUpdateResolution886Assessment();

	const activities_options = newActivities.map((activity) => ({
		label: activity.name,
		value: activity.id
	}));

	const startIndex = (currentPage - DEFAULT_PAGE) * limit;
	const endIndex = startIndex + limit;
	const paginated_activities = files_activities.slice(startIndex, endIndex);

	function handleSetPage(page: number): void {
		setCurrentPage(page);
	}

	const handleChange = (index: number, value: string): void => {
		const newSelections = [...selections];
		newSelections[index] = value;
		setSelections(newSelections);
	};

	const getAvailableOptions = (index: number) => {
		return activities_options.filter((opt) => !selections.includes(opt.value) || selections[index] === opt.value);
	};

	function useCustomBreakpoint() {
		const [isXl, setIsXl] = useState(false);

		useEffect(() => {
			const handleResize = () => {
				setIsXl(window.innerWidth >= BREAKPOINT_CONFIG.XL_WIDTH);
			};

			handleResize();
			window.addEventListener('resize', handleResize);
			return () => window.removeEventListener('resize', handleResize);
		}, []);

		return { isXl };
	}

	function handleInputChange(event: React.ChangeEvent<HTMLInputElement>): void {
		setName(event.target.value);
	}

	function addItem(e: React.MouseEvent<HTMLAnchorElement>): void {
		e.preventDefault();

		if (!name.trim()) return;

		createActivity(
			{
				name: name.trim(),
				workstation_id,
				organization_id,
				company_id
			},
			{
				onSuccess: () => {
					setName('');
				}
			}
		);
	}

	function handleEvaluation(file_id: string, assessment_id: string): void {
		handleSetCategoryResultId('');

		const activity_id = form.getFieldValue([`selected_${file_id}`]);
		const review_id = form.getFieldValue(['general_information', 'review_id']);

		const payload: UpdateResolution886AssessmentParams = {
			organization_id,
			company_id,
			review_id,
			activity_id,
			file_id,
			assessment_id
		};

		updateAssessment(payload, {
			onSuccess: ({ data }) => {
				setAssessment(data as unknown as AssessmentResponse);
				goToSubStep(2);
			}
		});
	}

	function setEvaluationName(status: string) {
		if (newReview || previousReviewData) {
			if (status === 'NOT_AVALIATED') {
				return I18n.get('Review not started');
			}

			return I18n.get('Review completed');
		} else {
			if (status === 'NOT_AVALIATED') {
				return I18n.get('Evaluation not started');
			}

			return I18n.get('Evaluation completed');
		}
	}

	async function handleCheckEvaluation() {
		const activities = form.getFieldValue(['evaluation', 'activities']);

		const activities_not_evaluated = await activities.some(
			(activity: Activity) => activity.status === 'NOT_AVALIATED'
		);

		if (activities_not_evaluated) {
			setAssessmentsNotAvaliated(true);
			setTimeout(() => setAssessmentsNotAvaliated(false), BREAKPOINT_CONFIG.TOAST_DURATION);
		} else {
			setAssessmentsNotAvaliated(false);
			handleUpdateStep(StepsResolution886.ACTION_PLAN);
		}
	}

	useEffect(() => {
		setLimit(isXl ? BREAKPOINT_CONFIG.PAGINATION_LIMITS.XL : BREAKPOINT_CONFIG.PAGINATION_LIMITS.DEFAULT);
		handleSetPage(DEFAULT_PAGE);
	}, [isXl]);

	useEffect(() => {
		const initialSelections = files_activities.map(
			(file: ActivitiesValuesProps) => form.getFieldValue([`selected_${file.file_id}`]) || null
		);
		setSelections(initialSelections);
	}, [files_activities, form]);

	useEffect(() => {
		if (!isFetchingPreviousReview) {
			if (previousReviewData && previousReviewData.id) {
				setPreviousAssessments(previousReviewData.assessments as unknown as AssessmentResponse[]);
			}
		}
	}, [previousReviewData, isFetchingPreviousReview]);

	useEffect(() => {
		if (!isFetchingActivities && !isFetchingPreviousReview) {
			if (previousAssessments.length) {
				setNewActivities(
					activities.filter((activity: Activity) =>
						previousAssessments.some((assessment) => assessment.activity?.id === activity.id)
					)
				);
			} else {
				setNewActivities(activities as unknown as Activity[]);
			}
		}
	}, [previousAssessments, activities, isFetchingActivities, isFetchingPreviousReview]);

	const isLoading =
		isFetchingActivities || isLoadingCreateActivity || isLoadingUpdateAssessment || isFetchingPreviousReview;

	return {
		isXl,
		limit,
		isLoading,
		newReview,
		currentPage,
		files_activities,
		paginated_activities,
		assessmentsNotAvaliated,
		activities: activities_options,
		handleCheckEvaluation,
		getAvailableOptions,
		handleInputChange,
		setEvaluationName,
		handleEvaluation,
		handleUpdateStep,
		handleSetPage,
		handleChange,
		setLimit,
		addItem
	};
}
